import { useCallback, useMemo, useRef, useState } from 'react';

export interface BarcodeQueueItem {
  id: string;
  source: 'scan' | 'manual';
  createdAt: number;
  quantity: number;
}

export interface UseBarcodeQueueOptions {
  duplicateWindowMs?: number;
}

export interface UseBarcodeQueueResult {
  queue: BarcodeQueueItem[];
  push: (id: string, source?: 'scan' | 'manual', quantity?: number) => BarcodeQueueItem | null;
  acknowledge: (id: string) => void;
  isDuplicate: (id: string) => boolean;
}

const DEFAULT_WINDOW = 10_000;

export function useBarcodeQueue(options: UseBarcodeQueueOptions = {}): UseBarcodeQueueResult {
  const { duplicateWindowMs = DEFAULT_WINDOW } = options;
  const [queue, setQueue] = useState<BarcodeQueueItem[]>([]);
  const lastSeenRef = useRef<Map<string, number>>(new Map());

  const isDuplicate = useCallback(
    (id: string) => {
      const last = lastSeenRef.current.get(id);
      if (!last) return false;
      return Date.now() - last < duplicateWindowMs;
    },
    [duplicateWindowMs]
  );

  const push = useCallback(
    (id: string, source: 'scan' | 'manual' = 'scan', quantity = 1) => {
      if (!id) return null;
      const now = Date.now();
      lastSeenRef.current.set(id, now);
      const item: BarcodeQueueItem = { id, source, createdAt: now, quantity };
      setQueue(prev => [...prev, item]);
      return item;
    },
    []
  );

  const acknowledge = useCallback((id: string) => {
    setQueue(prev => prev.filter(entry => entry.id !== id));
  }, []);

  return useMemo(
    () => ({ queue, push, acknowledge, isDuplicate }),
    [acknowledge, isDuplicate, push, queue]
  );
}
